import type { DestinationIdentity } from '@/services/observability-api';

export type DestinationIdentityDetailsProps = {
  data?: DestinationIdentity | undefined;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
};

export function DestinationIdentityDetails({
  data,
  pending,
  failed,
  onRetry,
}: DestinationIdentityDetailsProps) {
  return (
    <section className="mt-5 space-y-2 text-sm" aria-label="Destination identity clues">
      <h3 className="font-semibold text-neutral-900">Destination identity clues</h3>
      <p className="text-xs text-neutral-500">
        Recent dependency samples from this caller. These are clues, not an exact match to the graph
        edge. Traces are sampled and may arrive later.
      </p>
      {pending ? (
        <p role="status">Loading identity details...</p>
      ) : failed ? (
        <>
          <p role="alert">Destination identity details are temporarily unavailable.</p>
          <button
            type="button"
            className="font-medium text-lavender-700 underline"
            onClick={onRetry}
          >
            Retry identity lookup
          </button>
        </>
      ) : data ? (
        <>
          {data.samples.length === 0 ? (
            <p>No unidentified destination samples found in this time range.</p>
          ) : (
            <ul className="space-y-2">
              {data.samples.map((sample, index) => (
                <li key={index} className="rounded bg-neutral-50 p-2">
                  <p className="break-all font-medium">
                    {sample.hostname ?? 'Hostname unavailable or withheld'}
                  </p>
                  <p className="text-xs text-neutral-600">
                    {sample.category.toUpperCase()} · {sample.count} sampled dependencies
                  </p>
                  <p className="text-xs text-neutral-500">
                    Last seen {new Date(sample.lastSeen).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {data.truncated && (
            <p className="text-xs text-neutral-500">
              Only a limited set of recent samples is shown.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}
